import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../../../contexts/AuthContext';
import { DEFAULT_VIRTUAL_PET_SETTINGS, PET_BALANCE, InteractionType, PlayActionType } from '../config/petBalance';
import {
  applyElapsedDecay,
  applyEvolution,
  applyInteraction,
  applyLevelUp,
  applyPlayAction,
  addNurtureTrait,
  addPetAbility,
  clamp,
  getDailyInteractionRemaining,
  getDungeonEnergyCost,
  getEvolutionStatus,
  getStrokeFreeRemaining,
  mergeStats,
  normalizeDailyActions,
  recordDailyInteraction
} from '../domain/petEngine';
import { PET_PLAY_OPTIONS } from '../config/petPlayActions';
import {
  DungeonDifficulty,
  PET_SHOP_ITEMS,
  PetDungeonRun,
  PetEvolutionState,
  PetInventory,
  PetProfile,
  PetShareCard,
  PetStats,
  VirtualPetClassSettings
} from '../types';
import { StudentPath, virtualPetService } from '../services/virtualPetService';
import type { OwnedPetConsumable, OwnedPetPlayTicket } from '../../../../../types/petStore';

export const useVirtualPet = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>('');
  const [profile, setProfile] = useState<PetProfile | null>(null);
  const [stats, setStats] = useState<PetStats | null>(null);
  const [inventory, setInventory] = useState<PetInventory | null>(null);
  const [evolution, setEvolution] = useState<PetEvolutionState | null>(null);
  const [studentPoints, setStudentPoints] = useState(0);
  const [shares, setShares] = useState<PetShareCard[]>([]);
  const [dungeonRuns, setDungeonRuns] = useState<PetDungeonRun[]>([]);
  const [ownedPetConsumables, setOwnedPetConsumables] = useState<OwnedPetConsumable[]>([]);
  const [ownedPlayTickets, setOwnedPlayTickets] = useState<OwnedPetPlayTicket[]>([]);
  const [isEating, setIsEating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSleeping, setIsSleeping] = useState(false);
  const [isEvolving, setIsEvolving] = useState(false);
  const [classSettings, setClassSettings] = useState<VirtualPetClassSettings>(DEFAULT_VIRTUAL_PET_SETTINGS);

  const lastInteractAtRef = useRef(0);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const evolveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipPersistRef = useRef(true);
  const stateRef = useRef<{
    profile: PetProfile | null;
    stats: PetStats | null;
    inventory: PetInventory | null;
    evolution: PetEvolutionState | null;
  }>({ profile: null, stats: null, inventory: null, evolution: null });

  useEffect(() => {
    stateRef.current = { profile, stats, inventory, evolution };
  }, [profile, stats, inventory, evolution]);

  useEffect(() => () => {
    if (evolveTimerRef.current) clearTimeout(evolveTimerRef.current);
  }, []);

  const [path, setPath] = useState<StudentPath | null>(null);
  const [pathLoading, setPathLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolvePath = async () => {
      setPathLoading(true);
      if (!currentUser?.uid || !currentUser?.schoolId) {
        if (!cancelled) {
          setPath(null);
          setPathLoading(false);
        }
        return;
      }

      try {
        const classDetails = currentUser as unknown as { grade?: unknown; class?: unknown };
        const resolved = await virtualPetService.resolveStudentPath({
          authUid: currentUser.uid,
          schoolId: currentUser.schoolId,
          classId: currentUser.classId,
          grade: typeof classDetails.grade === 'number' ? classDetails.grade : undefined,
          class: typeof classDetails.class === 'number' ? classDetails.class : undefined
        });
        if (!cancelled) {
          setPath(resolved);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setPath(null);
        }
      } finally {
        if (!cancelled) {
          setPathLoading(false);
        }
      }
    };

    resolvePath();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const schedulePersist = useCallback(() => {
    if (!path) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const { profile: p, stats: s, inventory: i, evolution: e } = stateRef.current;
      if (!p || !s || !i || !e) return;
      virtualPetService.persistState(path, p, s, i, e).catch((error) => {
        console.error(error);
      });
    }, PET_BALANCE.persistDebounceMs);
  }, [path]);

  const applyDecayTick = useCallback(() => {
    setStats((prev) => {
      if (!prev) return prev;
      return applyElapsedDecay(prev);
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!path) {
      setLoading(false);
      setProfile(null);
      setStats(null);
      setInventory(null);
      setEvolution(null);
      setMessage('학급 정보가 없어 캐릭터 허브를 불러올 수 없어요. 학급 배정 후 다시 시도해 주세요.');
      return;
    }
    setLoading(true);
    try {
      const [state, settings] = await Promise.all([
        virtualPetService.loadState(path),
        virtualPetService.loadClassSettings(path)
      ]);
      setClassSettings(settings);
      skipPersistRef.current = true;
      setProfile(applyLevelUp(state.profile));
      setStats(state.stats);
      setInventory(state.inventory);
      setEvolution(state.evolution);
      setStudentPoints(state.studentPoints);
      if (state.migrated) {
        setMessage(
          '별알을 키우기 시작해요!'
        );
      } else {
        setMessage('');
      }

      const [shareResult, runsResult, petConsumablesResult, playTicketsResult] =
        await Promise.allSettled([
        virtualPetService.listShares(path),
        virtualPetService.listDungeonRuns(path),
        virtualPetService.loadOwnedPetConsumables(path),
        virtualPetService.loadOwnedPlayTickets(path)
      ]);
      setShares(shareResult.status === 'fulfilled' ? shareResult.value : []);
      setDungeonRuns(runsResult.status === 'fulfilled' ? runsResult.value : []);
      setOwnedPetConsumables(petConsumablesResult.status === 'fulfilled' ? petConsumablesResult.value : []);
      setOwnedPlayTickets(playTicketsResult.status === 'fulfilled' ? playTicketsResult.value : []);
      if (
        shareResult.status === 'rejected' ||
        runsResult.status === 'rejected' ||
        petConsumablesResult.status === 'rejected' ||
        playTicketsResult.status === 'rejected'
      ) {
        console.error('VirtualPet secondary load failed', {
          shares: shareResult.status === 'rejected' ? shareResult.reason : null,
          runs: runsResult.status === 'rejected' ? runsResult.reason : null,
          petConsumables: petConsumablesResult.status === 'rejected' ? petConsumablesResult.reason : null,
          playTickets: playTicketsResult.status === 'rejected' ? playTicketsResult.reason : null
        });
      }
    } catch (error) {
      console.error(error);
      setProfile(null);
      setStats(null);
      setInventory(null);
      setEvolution(null);
      setMessage('캐릭터 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
      setTimeout(() => {
        skipPersistRef.current = false;
      }, 0);
    }
  }, [path]);

  useEffect(() => {
    if (pathLoading) return;
    refresh();
  }, [refresh, pathLoading]);

  useEffect(() => {
    if (skipPersistRef.current) return;
    schedulePersist();
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [profile, stats, inventory, evolution, schedulePersist]);

  useEffect(() => {
    const intervalSec = PET_BALANCE.decayRefreshIntervalSec;
    const id = setInterval(() => applyDecayTick(), intervalSec * 1000);
    return () => clearInterval(id);
  }, [applyDecayTick]);

  const triggerAnimation = useCallback((type: InteractionType) => {
    if (type === 'feed') {
      setIsEating(true);
      setTimeout(() => setIsEating(false), 2000);
    } else if (type === 'play') {
      setIsPlaying(true);
      setTimeout(() => setIsPlaying(false), 3000);
    } else if (type === 'rest') {
      setIsSleeping(true);
      setTimeout(() => setIsSleeping(false), 3000);
    }
  }, []);

  const interact = useCallback(
    async (type: InteractionType) => {
      if (!profile || !stats || !path) return;

      if (type === 'feed') {
        setMessage('간식은 학급 상점에서 구매한 뒤 사용해 주세요.');
        return;
      }

      if (type === 'play') {
        setMessage('놀아주기 메뉴에서 활동을 선택해 주세요.');
        return;
      }

      const workingProfile = normalizeDailyActions(profile);
      const remaining = getDailyInteractionRemaining(workingProfile, type, {
        chat: classSettings.freeChatPerDay,
        rest: classSettings.freeRestPerDay
      });
      if (remaining <= 0) {
        const limit = type === 'chat' ? classSettings.freeChatPerDay : classSettings.freeRestPerDay;
        setMessage(`오늘 ${type === 'chat' ? '대화' : '휴식'} ${limit}회를 모두 사용했어요. 내일 다시 만나요!`);
        return;
      }

      const now = Date.now();
      if (now - lastInteractAtRef.current < classSettings.interactionCooldownSec * 1000) {
        setMessage(`잠시 후에 다시 시도해 주세요. (${classSettings.interactionCooldownSec}초)`);
        return;
      }
      lastInteractAtRef.current = now;

      const decayed = applyElapsedDecay(stats);
      const result = applyInteraction(type, workingProfile, decayed);
      const nextProfile = recordDailyInteraction(result.profile, type);
      setProfile(nextProfile);
      setStats(result.stats);
      triggerAnimation(type);

      const labels: Record<Exclude<InteractionType, 'feed' | 'play'>, string> = {
        chat: '캐릭터와 대화했어요!',
        rest: '캐릭터가 푹 쉬었어요!'
      };
      setMessage(`${labels[type]} 오늘 ${remaining - 1}회 남았어요.`);
    },
    [profile, stats, path, triggerAnimation, classSettings]
  );

  const performPlayAction = useCallback(
    async (action: PlayActionType) => {
      if (!profile || !stats || !path) return;

      const now = Date.now();
      if (now - lastInteractAtRef.current < classSettings.interactionCooldownSec * 1000) {
        setMessage(`잠시 후에 다시 시도해 주세요. (${classSettings.interactionCooldownSec}초)`);
        return;
      }

      let workingProfile = normalizeDailyActions(profile);
      let incrementStrokeCount = false;

      const consumeTicketForAction = async (actionType: PlayActionType): Promise<boolean> => {
        const ticket = ownedPlayTickets.find((item) => item.petPlayAction === actionType);
        if (!ticket) return false;
        try {
          await virtualPetService.consumePlayTicket(path, ticket.uniqueKey);
          setOwnedPlayTickets((prev) => prev.filter((item) => item.uniqueKey !== ticket.uniqueKey));
          return true;
        } catch (error) {
          console.error(error);
          setMessage(error instanceof Error ? error.message : '이용권 사용에 실패했어요.');
          const refreshed = await virtualPetService.loadOwnedPlayTickets(path);
          setOwnedPlayTickets(refreshed);
          return false;
        }
      };

      if (action === 'stroke') {
        const freeRemaining = getStrokeFreeRemaining(workingProfile, classSettings.freeStrokePerDay);
        if (freeRemaining > 0) {
          incrementStrokeCount = true;
        } else {
          const used = await consumeTicketForAction('stroke');
          if (!used) {
            setMessage(
              `오늘 무료 쓰다듬기 ${classSettings.freeStrokePerDay}회를 모두 썼어요. 학급 상점에서 쓰다듬기 이용권을 구매해 주세요.`
            );
            return;
          }
        }
      } else {
        const used = await consumeTicketForAction(action);
        if (!used) {
          const label = PET_PLAY_OPTIONS.find((option) => option.id === action)?.name || '놀아주기';
          setMessage(`학급 상점에서 ${label} 이용권을 구매해 주세요.`);
          return;
        }
      }

      lastInteractAtRef.current = now;
      const decayed = applyElapsedDecay(stats);
      const result = applyPlayAction(action, workingProfile, decayed, {
        incrementStrokeCount
      });
      setProfile(result.profile);
      setStats(result.stats);
      triggerAnimation('play');

      const label = PET_PLAY_OPTIONS.find((option) => option.id === action)?.name || '놀아주기';
      setMessage(`${label}로 캐릭터와 놀았어요!`);
    },
    [profile, stats, path, ownedPlayTickets, triggerAnimation, classSettings]
  );

  const playActionStatus = useMemo(() => {
    const countTickets = (action: PlayActionType) =>
      ownedPlayTickets.filter((item) => item.petPlayAction === action).length;

    const strokeFreeRemaining = profile
      ? getStrokeFreeRemaining(normalizeDailyActions(profile), classSettings.freeStrokePerDay)
      : classSettings.freeStrokePerDay;
    const strokeTicketCount = countTickets('stroke');

    return {
      stroke: {
        freeRemaining: strokeFreeRemaining,
        ticketCount: strokeTicketCount,
        canPlay: strokeFreeRemaining > 0 || strokeTicketCount > 0
      },
      ball: {
        ticketCount: countTickets('ball'),
        canPlay: countTickets('ball') > 0
      },
      feather: {
        ticketCount: countTickets('feather'),
        canPlay: countTickets('feather') > 0
      },
      laser: {
        ticketCount: countTickets('laser'),
        canPlay: countTickets('laser') > 0
      }
    } as Record<
      PlayActionType,
      { ticketCount: number; canPlay: boolean; freeRemaining?: number }
    >;
  }, [profile, ownedPlayTickets, classSettings.freeStrokePerDay]);

  const dailyInteractionStatus = useMemo(() => ({
    chat: profile ? getDailyInteractionRemaining(profile, 'chat', { chat: classSettings.freeChatPerDay, rest: classSettings.freeRestPerDay }) : classSettings.freeChatPerDay,
    rest: profile ? getDailyInteractionRemaining(profile, 'rest', { chat: classSettings.freeChatPerDay, rest: classSettings.freeRestPerDay }) : classSettings.freeRestPerDay
  }), [profile, classSettings]);

  const useConsumable = useCallback(
    async (uniqueKey: string) => {
      if (!path || !profile || !stats) return;

      const owned = ownedPetConsumables.find((item) => item.uniqueKey === uniqueKey);
      if (!owned) {
        setMessage('간식이 없어요. 학급 상점에서 구매해 주세요.');
        return;
      }

      const now = Date.now();
      if (now - lastInteractAtRef.current < classSettings.interactionCooldownSec * 1000) {
        setMessage(`잠시 후에 다시 시도해 주세요. (${classSettings.interactionCooldownSec}초)`);
        return;
      }
      lastInteractAtRef.current = now;

      try {
        const consumed = await virtualPetService.consumePetConsumable(path, uniqueKey);
        const effect = consumed.petEffect || owned.petEffect;

        const decayed = applyElapsedDecay(stats);
        setStats(
          mergeStats(decayed, {
            hunger: effect?.hunger || 0,
            happiness: effect?.happiness || 0,
            energy: effect?.energy || 0,
            health: effect?.health || 0
          })
        );
        setProfile((prev) => {
          if (!prev) return prev;
          return addPetAbility(addNurtureTrait(applyLevelUp({
            ...prev,
            exp: prev.exp + (effect?.exp || 0),
            intimacy: clamp(prev.intimacy + (effect?.intimacy || 0))
          }), 'care', 2), 'vitality', 0.1);
        });
        setOwnedPetConsumables((prev) => prev.filter((item) => item.uniqueKey !== uniqueKey));
        triggerAnimation('feed');
        setMessage(`${consumed.name}을(를) 사용했어요!`);
      } catch (error) {
        console.error(error);
        setMessage(error instanceof Error ? error.message : '간식 사용에 실패했어요.');
        const refreshed = await virtualPetService.loadOwnedPetConsumables(path);
        setOwnedPetConsumables(refreshed);
      }
    },
    [path, profile, stats, ownedPetConsumables, triggerAnimation, classSettings.interactionCooldownSec]
  );

  const buyItem = useCallback(
    async (itemId: string) => {
      if (!path || !inventory || !profile) return;
      const item = PET_SHOP_ITEMS.find((it) => it.id === itemId);
      if (!item) return;
      try {
        if (item.slot !== 'consumable' && inventory.ownedItemIds.includes(item.id)) {
          setMessage('이미 구매한 아이템입니다.');
          return;
        }

        const points = await virtualPetService.buyItem(path, item.id);
        setStudentPoints(points);

        setInventory((prev) => {
          if (!prev) return prev;
          if (prev.ownedItemIds.includes(item.id)) return prev;
          return { ...prev, ownedItemIds: [...prev.ownedItemIds, item.id] };
        });

        setMessage(`${item.name} 구매 완료`);
      } catch (error) {
        console.error(error);
        if (error instanceof Error && error.message) {
          setMessage(error.message);
          return;
        }
        setMessage('아이템 구매에 실패했어요.');
      }
    },
    [path, inventory, profile]
  );

  const equipItem = useCallback(
    (itemId: string) => {
      if (!inventory) return;
      const item = PET_SHOP_ITEMS.find((it) => it.id === itemId);
      if (!item || item.slot === 'consumable') return;
      if (!inventory.ownedItemIds.includes(itemId)) {
        setMessage('먼저 구매해야 장착할 수 있어요.');
        return;
      }
      setInventory((prev) => {
        if (!prev) return prev;
        if (item.slot === 'hat') return { ...prev, equipped: { ...prev.equipped, hat: itemId } };
        if (item.slot === 'accessory') return { ...prev, equipped: { ...prev.equipped, accessory: itemId } };
        return { ...prev, equipped: { ...prev.equipped, background: itemId } };
      });
      setMessage(`${item.name} 장착 완료`);
    },
    [inventory]
  );

  const shareCharacter = useCallback(async () => {
    if (!path || !profile || !inventory || !currentUser) return;
    try {
      await virtualPetService.createShare(path, {
        ownerUid: currentUser.uid,
        ownerName: currentUser.name || '학생',
        petName: profile.name,
        petForm: profile.petForm,
        level: profile.level,
        equipped: inventory.equipped
      });
      setMessage('캐릭터를 반 친구들과 공유했어요.');
      const shareList = await virtualPetService.listShares(path);
      setShares(shareList);
    } catch (error) {
      console.error(error);
      setMessage('캐릭터 공유에 실패했어요.');
    }
  }, [path, profile, inventory, currentUser]);

  const startDungeon = useCallback(
    async (difficulty: DungeonDifficulty) => {
      if (!path || !stats) return;
      const required = getDungeonEnergyCost(difficulty);
      const hasRunningDungeon = dungeonRuns.some((run) => run.status === 'running');
      if (hasRunningDungeon) {
        setMessage('이미 진행 중인 던전이 있어요.');
        return;
      }
      const decayed = applyElapsedDecay(stats);
      if (decayed.energy < required) {
        setMessage(`에너지가 부족해요. (${required} 필요)`);
        return;
      }
      try {
        setStats(mergeStats(decayed, { energy: -required }));
        await virtualPetService.startDungeon(path, difficulty);
        setMessage('던전 파견을 시작했어요.');
        const runs = await virtualPetService.listDungeonRuns(path);
        setDungeonRuns(runs);
      } catch (error) {
        console.error(error);
        setMessage('던전 파견에 실패했어요.');
        await refresh();
      }
    },
    [path, stats, dungeonRuns, refresh]
  );

  const claimDungeonReward = useCallback(
    async (runId: string) => {
      if (!path) return;
      const run = dungeonRuns.find((item) => item.id === runId);
      if (!run) {
        setMessage('던전 기록을 찾을 수 없어요.');
        return;
      }
      if (run.status !== 'completed') {
        setMessage('보상 수령 가능한 던전이 아닙니다.');
        return;
      }
      try {
        await virtualPetService.claimDungeonReward(path, runId);
        const [state, runs] = await Promise.all([
          virtualPetService.loadState(path),
          virtualPetService.listDungeonRuns(path)
        ]);
        skipPersistRef.current = true;
        setProfile(applyLevelUp(state.profile));
        setStats(state.stats);
        setInventory(state.inventory);
        setStudentPoints(state.studentPoints);
        setEvolution(state.evolution);
        setDungeonRuns(runs);
        setMessage('던전 보상 수령 완료');
        setTimeout(() => {
          skipPersistRef.current = false;
        }, 0);
      } catch (error) {
        console.error(error);
        if (error instanceof Error && error.message) {
          setMessage(error.message);
          return;
        }
        setMessage('던전 보상 수령에 실패했어요.');
      }
    },
    [path, dungeonRuns]
  );

  const evolve = useCallback(() => {
    if (!profile || !evolution || isEvolving) return;
    const result = applyEvolution(profile, evolution);
    if (!result) {
      setMessage(getEvolutionStatus(profile, evolution).nextForm ? '진화 조건이 아직 부족해요.' : '최종 진화 상태입니다.');
      return;
    }
    setIsEvolving(true);
    setMessage(profile.petForm === 'starEgg' ? '별알이 부화하려고 해요!' : '진화의 빛이 모이고 있어요!');
    evolveTimerRef.current = setTimeout(() => {
      setProfile(result.profile);
      setEvolution(result.evolution);
      setIsEvolving(false);
      setMessage('진화에 성공했어요! 새로운 모습으로 성장했습니다.');
      evolveTimerRef.current = null;
    }, 1700);
  }, [profile, evolution, isEvolving]);

  const evolutionStatus = useMemo(() => {
    if (!profile || !evolution) return null;
    return getEvolutionStatus(profile, evolution);
  }, [profile, evolution]);

  return {
    loading: pathLoading || loading,
    message,
    profile,
    stats,
    inventory,
    evolution,
    studentPoints,
    shares,
    dungeonRuns,
    ownedPetConsumables,
    ownedPlayTickets,
    playActionStatus,
    dailyInteractionStatus,
    playOptions: PET_PLAY_OPTIONS,
    isEating,
    isPlaying,
    isSleeping,
    isEvolving,
    evolutionStatus,
    classSettings,
    shopItems: PET_SHOP_ITEMS,
    actions: {
      interact,
      buyItem,
      useConsumable,
      performPlayAction,
      equipItem,
      shareCharacter,
      startDungeon,
      claimDungeonReward,
      evolve,
      refresh
    }
  };
};
